import {
    ChangeDetectionStrategy,
    Component,
    ComponentRef,
    computed,
    effect,
    inject,
    signal,
    untracked,
    viewChild,
} from '@angular/core';
import { StepHostDirective } from 'app/shared/directives/step-host.directive';
import { DamageReportStep } from '../../models/damage-report-step';
import { DamageReportStepConfig, getStepsForCurrentProduct } from '../../models/damage-report-step-config';
import { CreateDamageReportService } from '../../create-damage-report.service';
import { ActivatedRoute, DefaultUrlSerializer, Router } from '@angular/router';
import { Location } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { clampNumber, setComponentProps } from '@co-3/ctrl-ui/common';
import { CtrlIconComponent } from '@co-3/ctrl-ui/icons';
import { getCanonicalProduct } from 'app/customer-portal/fns/get-canonical-product';
import { switchMap } from 'rxjs';

@Component({
    selector: 'app-damage-report-container-view',
    imports: [StepHostDirective, CtrlIconComponent],
    templateUrl: './damage-report-container-view.component.html',
    styleUrl: './damage-report-container-view.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DamageReportContainerViewComponent {
    private createDamageReportService = inject(CreateDamageReportService);
    private location = inject(Location);
    private router = inject(Router);
    private activatedRoute = inject(ActivatedRoute);

    private routeParams = toSignal(this.activatedRoute.params);

    protected contract = toSignal(this.createDamageReportService.selectedContract$);

    private steps = getStepsForCurrentProduct(this.contract);
    private currentStepIndex = signal(0);
    private currentStep = computed(() => this.steps().at(this.currentStepIndex()));

    private stepHost = viewChild<StepHostDirective>(StepHostDirective);

    protected product = getCanonicalProduct(this.contract);

    public constructor() {
        // reset step index when steps update
        effect(() => {
            this.steps();
            this.goToStep(0);
        });
        // update the current step from url changes
        effect(() => {
            const slug = this.routeParams().step;

            untracked(() => this.goToStep(slug));
        });
        // set the current contract when contract id in url updates
        effect(() => {
            const contractId = parseInt(this.routeParams().contractId, 10);

            if (!isNaN(contractId)) {
                untracked(() => this.createDamageReportService.selectContract(contractId));
            }
        });
        // load the component for the current step
        effect(() => {
            const stepConfig = this.currentStep();

            untracked(() => {
                if (this.loadStepComponent(stepConfig)) {
                    // if the component has been loaded successfully
                    // update the current location
                    this.updateLocationForStep(stepConfig);
                } else {
                    // if the attempt to load the current component fails,
                    // try again with the component before it.
                    this.goToPreviousStep();
                }
            });
        });
    }

    // #region handle user interaction

    private goToNextStep(): void {
        this.goToStep(this.currentStepIndex() + 1);
    }

    private goToPreviousStep(): void {
        this.goToStep(this.currentStepIndex() - 1);
    }

    private goToStep(slugOrIndex: string | number | undefined): void {
        if (typeof slugOrIndex === 'number') {
            const index = clampNumber(slugOrIndex, 0, this.steps().length - 1);
            this.currentStepIndex.set(index);
            return;
        }
        if (typeof slugOrIndex === 'string') {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const index = this.steps().findIndex((step: DamageReportStepConfig<any>) => step.slug === slugOrIndex);
            if (index !== -1) {
                this.currentStepIndex.set(index);
            } else {
                this.goToStep(0);
            }
            return;
        }
        this.goToStep(0);
    }

    private handleSubmit(): void {
        this.createDamageReportService
            .submit()
            ?.pipe(
                switchMap((damageReport) =>
                    this.router.navigate(
                        ['/', 'portal', 'meine-vertraege', this.contract().id, 'gemeldete-schaeden', damageReport.id],
                        { queryParams: { just_submitted: true } },
                    ),
                ),
            )
            .subscribe(() => this.createDamageReportService.reset());
    }

    protected handleCancel(): void {
        if (
            this.createDamageReportService.pristine ||
            confirm('Bist Du sicher, dass Du abbrechen möchtest?\n\nAlle ungespeicherten Daten gehen verloren')
        ) {
            this.router
                .navigate(['/portal/meine-vertraege/', this.contract().id])
                .then(() => this.createDamageReportService.reset());
        }
    }

    // #endregion

    // #region handle component loading

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private loadStepComponent(stepConfig: DamageReportStepConfig<any>): ComponentRef<DamageReportStep> | undefined {
        if (!this.createDamageReportService.canActivate(stepConfig)) {
            return;
        }
        this.stepHost().viewContainerRef.clear();
        const componentRef = this.stepHost().viewContainerRef.createComponent<DamageReportStep>(stepConfig.component);

        setComponentProps(componentRef, { isFirst: this.currentStepIndex() === 0 });
        setComponentProps(componentRef, stepConfig.config ?? {});

        this.bindStepControls(componentRef.instance);

        return componentRef;
    }

    private bindStepControls(instance: DamageReportStep): void {
        instance.onNext?.subscribe(() => this.goToNextStep());
        instance.onPrev?.subscribe(() => this.goToPreviousStep());
        instance.onCancel?.subscribe(() => this.handleCancel());
        instance.onSubmit?.subscribe(() => this.handleSubmit());
        instance.navigate?.subscribe((slug: string) => this.goToStep(slug));
    }

    // #endregion

    // #region handle location updates

    private updateLocationForStep<T extends DamageReportStep>(stepConfig: DamageReportStepConfig<T>): void {
        const url = this.getUrlForStep(stepConfig);
        if (!this.location.isCurrentPathEqualTo(url)) {
            this.location.go(url);
        }
    }

    private getUrlForStep<T extends DamageReportStep>(step: DamageReportStepConfig<T>): string {
        const newPath = [step.slug];
        if (this.routeParams().step) {
            newPath.unshift('..');
        }
        const urlTree = this.router.createUrlTree(newPath, {
            relativeTo: this.activatedRoute,
        });
        return new DefaultUrlSerializer().serialize(urlTree);
    }

    // #endregion
}
